/**
 * URL-prefix → forced color mode. Single source of truth: route groups in
 * separate Next.js chunks each attach their own colorPalette bridge instance, so
 * a React-only `<ForceColorMode>` doesn't survive cross-group navigation.
 * Routes whose mode is known purely from the URL should live here.
 */
export type ForcedColorMode = 'light' | 'dark';

export const FORCED_COLOR_MODE_PREFIXES: ReadonlyArray<{
    readonly prefix: string;
    // `null` opts a more-specific path out of a broader prefix's force. Matching is
    // first-match-wins (here and in the inlined FOUC script), so a `null` entry must
    // precede the broader rule it carves out of.
    readonly mode: ForcedColorMode | null;
}> = [
    { prefix: '/policy-center/', mode: 'light' },
    { prefix: '/market-integrity/', mode: 'light' },
    { prefix: '/research', mode: 'light' },
    { prefix: '/indices', mode: 'light' },
    // World Cup Wrapped story player (mobile webview) is always dark.
    { prefix: '/wrapped', mode: 'dark' },
    // NFL Highlight is a cinematic dark landing page.
    { prefix: '/highlight/nfl-2025', mode: 'dark' },
    // AuthenticatedWebView feedback form matches the in-app dark chrome.
    { prefix: '/app-feedback', mode: 'dark' },
    // Chalamet film landing is a cinematic dark design regardless of the user's theme preference.
    { prefix: '/chalamet', mode: 'dark' },
    // America250 "Years of American Predictions" campaign paints its own light and dark sections,
    // so force light as the deterministic base; the dark story beats use a scoped `.dark` wrapper.
    { prefix: '/america250', mode: 'light' },
    // "Kalshi for Small Business" hedging campaign page is a bespoke light design.
    { prefix: '/hedging', mode: 'light' },
    // The company-reports calendar is a bespoke dark design. It renders
    // <ForceColorMode> too, but that only applies once the page mounts, so
    // navigating in from a light page flashed white until it did.
    { prefix: '/company-reports', mode: 'dark' },
    // The Citizen Debt Forecast is a bespoke light research-report design. Its (research) group layout
    // forces light too, but naming the path here settles the palette before that mounts. The old
    // /gdp-scenarios and /nationaldebt paths now redirect here, so they need no entry of their own.
    { prefix: '/citizen-debt-forecast', mode: 'light' },
];

/** Returns the forced mode for a pathname, or `null` if the path is not forced. */
export function getForcedColorModeForPath(pathname: string): ForcedColorMode | null {
    for (const { prefix, mode } of FORCED_COLOR_MODE_PREFIXES) {
        if (pathname.startsWith(prefix)) return mode;
    }
    return null;
}
